import type { UseQueryOptions } from '@tanstack/react-query';

import { useQuery } from '@tanstack/react-query';

import { policyService } from '../services/policy.service';

import type { SearchResultViewModel } from '../types/SearchResultViewModel';
import type { PolicyViewModel } from '../types/PolicyViewModel';
import type { MovePolicyTargetCoverageViewModel } from '../types/MovePolicyTargetCoverageViewModel';
import type { PolicySearchResultViewModel } from '../types/PolicySearchResultViewModel';
import type { ApiResponse, ErrorResponse } from '../services/api.types';
import type {
  CodeDescriptionLookupOption,
  ClassCodeLookupOption,
  EndorsementTypeLookupOption,
  EndorsementDescriptorTypeLookupOption,
  MarketLookupOption,
  PolicySearchRequest,
  PolicySearchStaleRequestStrategy,
  StateLookupOption,
  PolicyPeriodRatingTypeLookupOption,
} from '../services/policy.service';

export type PolicyInformationLookups = {
  stateLookups: StateLookupOption[];
  marketLookups: MarketLookupOption[];
  legalNatureLookups: CodeDescriptionLookupOption[];
  policyTypeLookups: CodeDescriptionLookupOption[];
  policyPlanTypeLookups: CodeDescriptionLookupOption[];
  retroRatingLookups: CodeDescriptionLookupOption[];
  experienceRatingLookups: PolicyPeriodRatingTypeLookupOption[];
};

export type PolicySearchQueryIdentity = Readonly<{
  criteria: Omit<PolicySearchRequest, 'pageNumber' | 'pageSize' | 'sortField' | 'sortDirection'>;
  pagination: { pageNumber: number; pageSize: number };
  sort: { sortField?: string; sortDirection?: 'asc' | 'desc' };
}>;

export function createPolicySearchQueryIdentity(
  request: PolicySearchRequest | null
): PolicySearchQueryIdentity | null {
  if (!request) {
    return null;
  }

  return {
    criteria: {
      policyNumber: request.policyNumber,
      effectiveDate: request.effectiveDate,
      carrierId: request.carrierId,
      employerName: request.employerName,
      fein: request.fein,
      policyStatus: request.policyStatus,
      governingClass: request.governingClass,
      premiumRangeMin: request.premiumRangeMin,
      premiumRangeMax: request.premiumRangeMax,
      complianceStatus: request.complianceStatus,
      dateRangeFrom: request.dateRangeFrom,
      dateRangeTo: request.dateRangeTo,
      addressStreet: request.addressStreet,
      addressCity: request.addressCity,
      addressStateCode: request.addressStateCode,
      addressState: request.addressState,
      addressZip: request.addressZip,
    },
    pagination: {
      pageNumber: request.pageNumber ?? 1,
      pageSize: request.pageSize ?? 25,
    },
    sort: {
      sortField: request.sortField,
      sortDirection: request.sortDirection,
    },
  };
}

export const usePolicyById = (
  comboId: number,
  coverageId: number,
  policyId: number,
  options?: UseQueryOptions<ApiResponse<PolicyViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<PolicyViewModel>, ErrorResponse>({
    queryKey: ['policy', comboId, coverageId, policyId],
    queryFn: () => policyService.getPolicyById(comboId, coverageId, policyId),
    enabled: !!comboId && !!coverageId && !!policyId,
    ...options,
  });

export const useClassCodeLookups = (
  options?: UseQueryOptions<ApiResponse<ClassCodeLookupOption[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<ClassCodeLookupOption[]>, ErrorResponse>({
    queryKey: ['classCodeLookups'],
    queryFn: () => policyService.getClassCodeLookups(),
    ...options,
  });

export const useEndorsementTypeLookups = (
  options?: UseQueryOptions<ApiResponse<EndorsementTypeLookupOption[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<EndorsementTypeLookupOption[]>, ErrorResponse>({
    queryKey: ['endorsementTypeLookups'],
    queryFn: () => policyService.getEndorsementTypeLookups(),
    ...options,
  });

export const useEndorsementDescriptorTypeLookups = (
  options?: UseQueryOptions<ApiResponse<EndorsementDescriptorTypeLookupOption[]>, ErrorResponse>
) =>
  useQuery<ApiResponse<EndorsementDescriptorTypeLookupOption[]>, ErrorResponse>({
    queryKey: ['endorsementDescriptorTypeLookups'],
    queryFn: () => policyService.getEndorsementDescriptorTypeLookups(),
    ...options,
  });

export const usePolicyInformationLookups = (
  options?: UseQueryOptions<PolicyInformationLookups, ErrorResponse>
) =>
  useQuery<PolicyInformationLookups, ErrorResponse>({
    queryKey: ['policyInformationLookups'],
    queryFn: async () => {
      const [
        statesResponse,
        marketsResponse,
        legalNaturesResponse,
        policyTypesResponse,
        policyPlanTypesResponse,
        retroRatingsResponse,
        experienceRatingsResponse,
      ] = await Promise.all([
        policyService.getStateLookups(),
        policyService.getMarketLookups(),
        policyService.getPolicyLegalNatureLookups(),
        policyService.getPolicyTypeLookups(),
        policyService.getPolicyPlanTypeLookups(),
        policyService.getPolicyRetroRatingLookups(),
        policyService.getPolicyPeriodEditExperienceRatingTypeLookups(),
      ]);

      return {
        stateLookups: statesResponse.data ?? [],
        marketLookups: marketsResponse.data ?? [],
        legalNatureLookups: legalNaturesResponse.data ?? [],
        policyTypeLookups: policyTypesResponse.data ?? [],
        policyPlanTypeLookups: policyPlanTypesResponse.data ?? [],
        retroRatingLookups: retroRatingsResponse.data ?? [],
        experienceRatingLookups: experienceRatingsResponse.data ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useMoveTargetCoverage = (
  comboId: number,
  coverageId: number,
  policyId: number,
  targetCoverageId: number,
  options?: UseQueryOptions<ApiResponse<MovePolicyTargetCoverageViewModel>, ErrorResponse>
) =>
  useQuery<ApiResponse<MovePolicyTargetCoverageViewModel>, ErrorResponse>({
    queryKey: ['policyMoveTargetCoverage', comboId, coverageId, policyId, targetCoverageId],
    queryFn: () =>
      policyService.getMoveTargetCoverage({
        comboId,
        coverageId,
        policyId,
        targetCoverageId,
      }),
    enabled: !!comboId && !!coverageId && !!policyId && targetCoverageId >= 10000000,
    ...options,
  });

export const usePolicySearch = (
  request: PolicySearchRequest | null,
  settings?: {
    staleRequestStrategy?: PolicySearchStaleRequestStrategy;
  },
  options?: UseQueryOptions<
    ApiResponse<SearchResultViewModel<PolicySearchResultViewModel>>,
    ErrorResponse
  >
) =>
  {
    const staleRequestStrategy = settings?.staleRequestStrategy ?? 'sequence';
    const queryIdentity = createPolicySearchQueryIdentity(request);

    return useQuery<ApiResponse<SearchResultViewModel<PolicySearchResultViewModel>>, ErrorResponse>({
      queryKey: ['policySearch', queryIdentity],
      queryFn: ({ signal }) =>
        policyService.searchPolicies(request ?? {}, {
          signal,
          staleRequestStrategy,
        }),
      enabled: !!request,
      // Preserve the previous page payload while the next page is loading to prevent
      // transient empty states that can reset server-side grid pagination.
      placeholderData: (previousData) => previousData,
      retry: false,
      ...options,
    });
  };
